import {
  EditWorkflowAssistant,
  EditWorkflowType,
  Events,
  FlowResourceType,
  IAiModifiedResource,
  transformModifiedResourcesToPendingStatuses,
  useEditorStore,
  configureQuery,
  useFlowBuilder2GetOrCreateDraftLayoutMutation,
  ModifiedResourceEnums,
  makeAssistantChatHistoryStore,
} from '@diana/core';
import { useFlowBuilder2PageStore } from '../../Store/useFlowBuilder2PageStore';

export const EditAssistantChat = (props: {
  type: EditWorkflowType;
  resourceType: FlowResourceType;
  layoutId?: string;
  componentId?: string;
  allowFileUpload?: boolean;
}) => {
  const { type, resourceType, layoutId, componentId, allowFileUpload = false } = props;

  const flowId = useEditorStore((s) => s.flow?.id ?? '');
  const flowOriginId = useEditorStore((s) => s.flow?.properties?.originId ?? '');
  const updateFlow = useEditorStore((s) => s.updateFlow);
  const setLazyLayout = useEditorStore((s) => s.setLazyLayout);

  const useAssistantChatHistoryStore = makeAssistantChatHistoryStore(flowOriginId);
  const activeSessionId = useAssistantChatHistoryStore((state) => state.activeSessionId ?? '');

  const setAiPendingStatuses = useFlowBuilder2PageStore((s) => s.setAiPendingStatuses);
  const resetActiveContext = useFlowBuilder2PageStore((s) => s.resetActiveContext);

  const { mutateAsync: getOrCreateDraftLayoutAsync } =
    useFlowBuilder2GetOrCreateDraftLayoutMutation({
      onSuccess: (layout) => {
        setLazyLayout(layout.id, layout);
      },
    });

  const onEditFinished = async ({
    data,
    shouldRefetchFlow = false,
  }: {
    data: IAiModifiedResource[];
    shouldRefetchFlow?: boolean;
  }) => {
    const hasFlowChange = data.some(
      (item) => item.resource_type === ModifiedResourceEnums.Workflow,
    );
    if (hasFlowChange || shouldRefetchFlow) {
      const flowQuery = configureQuery.getOrCreateFlowDraft({ flowId });
      const flow = await flowQuery.fn({});
      updateFlow(flow);
    }

    const layoutChanges = data.filter(
      (item) => item.resource_type === ModifiedResourceEnums.Layout,
    );
    if (layoutChanges.length > 0) {
      await Promise.all(
        layoutChanges.map((resource) =>
          getOrCreateDraftLayoutAsync({ layoutId: resource.resource_id, flowId }),
        ),
      );
    }

    const pendingStatuses = transformModifiedResourcesToPendingStatuses(data);
    setAiPendingStatuses(pendingStatuses);

    const { flows, layouts } = pendingStatuses;
    if (!!flows[0]) {
      resetActiveContext();
    } else {
      Events.SetFlowBuilderActiveTask.post({ layoutId: layouts[0] });
    }
  };

  return (
    <EditWorkflowAssistant
      key={activeSessionId}
      type={type}
      resourceType={resourceType}
      flowId={flowId}
      flowOriginId={flowOriginId}
      layoutId={layoutId}
      componentId={componentId}
      onEditFinished={onEditFinished}
      allowFileUpload={allowFileUpload}
    />
  );
};
